/* Volatile — drawer/dialog controller for cart drawer, ATC sheet, and mobile nav.
 * (Search is handled separately in search.js — it's a header transform, not a dialog.)
 *
 * Cart drawer is server-rendered as a section, so we use Shopify's Section
 * Rendering API to refresh its inner HTML on every open and after every mutation
 * — otherwise the drawer DOM is frozen in time from initial page load and gets
 * out of sync with server cart state.
 */
(() => {
  const byData = (key) => document.querySelector(`[${key}]`);
  const cart = byData('data-cart-drawer');
  const menu = byData('data-menu-dialog');
  const atc = byData('data-atc-sheet');
  const getSizeChart = () => document.querySelector('[data-size-chart]');

  const open = (dlg) => {
    if (!dlg) return;
    if (typeof dlg.showModal === 'function' && !dlg.open) dlg.showModal();
  };
  const close = (dlg) => {
    if (!dlg) return;
    if (dlg.open && typeof dlg.close === 'function') dlg.close();
  };

  // Refresh-coalescing with rollover: if a refresh is already in-flight when
  // someone calls us again (e.g. /cart/add.js completes WHILE we're already
  // refreshing because the user clicked Visit cart at the same time), we
  // queue another round so the drawer ends up in sync with the latest cart
  // state instead of the stale snapshot the in-flight call captured.
  let refreshing = null;
  let pending = false;
  const fetchCartSection = async () => {
    const res = await fetch('/cart/update.js?sections=cart-drawer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      cache: 'no-store',
      credentials: 'same-origin',
      body: '{}',
    });
    if (!res.ok) return null;
    return res.json();
  };
  /* The item count the drawer's CURRENT markup was rendered for. `null`
     until something paints it, so the first open of a page still refreshes
     once. Lets `openCart()` skip a round trip when the drawer is already
     showing the cart the shared state holds. */
  let painted = null;

  const applyCart = (data) => {
    if (!data) return;
    /* ONLY treat the payload as cart STATE when it actually is one.

       Two shapes arrive here: `/cart/update.js` and `/cart/change.js`
       return the FULL CART (item_count + items) with sections, but
       `/cart/add.js` returns the ADDED LINE - no item_count, no items
       array - with the sections riding along. Feeding that line into
       setState() normalised to an EMPTY cart, which is exactly the
       flicker Aditya reported on the m-web PDP: optimistic "Added" ->
       line-shaped payload wipes the state (button reverts, bag badge
       vanishes) -> the background refresh() restores the truth a second
       later. The section HTML is always safe to paint; the state and
       the badge only update from a genuinely cart-shaped payload. */
    const isCart = typeof data.item_count === 'number' && Array.isArray(data.items);
    if (isCart && window.VolatileCart && typeof window.VolatileCart.setState === 'function') {
      window.VolatileCart.setState(data);
    }
    const html = data.sections && data.sections['cart-drawer'];
    if (html && cart) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-cart-drawer]');
      if (fresh) cart.innerHTML = fresh.innerHTML;
    }
    if (isCart) {
      document.querySelectorAll('[data-header-cart-count]').forEach((el) => {
        el.textContent = String(data.item_count);
        el.hidden = data.item_count === 0;
      });
      painted = data.item_count;
    }
  };
  const refreshCartDrawer = () => {
    if (refreshing) {
      pending = true;
      return refreshing;
    }
    refreshing = (async () => {
      try {
        do {
          pending = false;
          const data = await fetchCartSection();
          applyCart(data);
        } while (pending); // someone called us mid-fetch; do another round
      } catch (err) {
        console.warn('[VS/drawer] refreshCartDrawer failed', err);
      } finally {
        refreshing = null;
      }
    })();
    return refreshing;
  };

  const openCart = () => {
    // Open the drawer first so the user gets instant feedback. The cart
    // body is server-rendered in the section, so it's already accurate up
    // to the last navigation; refresh runs in the background and replaces
    // innerHTML when the response lands (typically a few hundred ms later).
    open(cart);
    /* Skip the round trip when the drawer is already showing the cart the
       shared state holds. Every mutation now repaints the drawer from its
       OWN response (add.js and change.js both return the section), so by
       the time the bag is opened the markup is usually already right, and
       refreshing anyway meant a request plus a visible innerHTML swap for
       no change. Any mismatch - a page that has not painted yet, a change
       made in another tab - still refreshes. */
    const known = window.VolatileCart && window.VolatileCart.state
      ? window.VolatileCart.state.item_count : null;
    if (painted !== null && known !== null && painted === known) return;
    refreshCartDrawer();
  };

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-open-menu]')) { e.preventDefault(); open(menu); }
    else if (e.target.closest('[data-open-cart]')) { e.preventDefault(); openCart(); }
    else if (e.target.closest('[data-open-size-chart]')) { e.preventDefault(); open(getSizeChart()); }
    else if (e.target.closest('[data-atc-view-cart]')) { e.preventDefault(); close(atc); openCart(); }

    if (e.target.closest('[data-close-menu]')) close(menu);
    if (e.target.closest('[data-close-cart]')) close(cart);
    if (e.target.closest('[data-close-atc]')) close(atc);
    if (e.target.closest('[data-close-size-chart]')) close(getSizeChart());
  });

  // Backdrop click
  [cart, menu, atc, getSizeChart()].forEach((dlg) => {
    if (!dlg) return;
    dlg.addEventListener('click', (e) => {
      if (e.target === dlg) close(dlg);
    });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') [cart, menu, atc, getSizeChart()].forEach(close);
  });

  window.VolatileUI = {
    openCart,
    openMenu: () => open(menu),
    openAtc: () => open(atc),
    openSizeChart: () => open(getSizeChart()),
    closeCart: () => close(cart),
    closeAtc: () => close(atc),
    closeSizeChart: () => close(getSizeChart()),
    refreshCartDrawer,
    /* Exported so a mutation that ALREADY has the section HTML in its own
       response can paint the drawer without spending another round trip on
       `refreshCartDrawer()`. `/cart/add.js` and `/cart/change.js` both
       return `sections` when asked, so the fetch that changed the cart is
       also the fetch that can redraw it. */
    applyCartSections: applyCart,
  };
})();
